import { Dispatch, SetStateAction } from 'react';
import { Chip, Stack, Tooltip, Typography } from '@mui/material';
import { Setting, Option } from '@/types';
import { SelectedSettings } from '../ProductDetails';
interface Props {
  settings?: Setting[];
  selectedSettings: SelectedSettings;
  setSelectedSettings: Dispatch<SetStateAction<SelectedSettings>>;
}

const ProductSettings = ({
  settings = [],
  selectedSettings,
  setSelectedSettings,
}: Props) => {
  const isLimitReached = (
    selectedSetting: Setting,
    optionTitle: Option['title']
  ) => {
    if (selectedSetting.type !== 'options') {
      return false;
    }

    if (!selectedSetting?.max) {
      return false;
    }

    const selectedOptions = selectedSettings[selectedSetting.title] ?? [];

    if (selectedOptions.includes(optionTitle)) {
      return false;
    }

    const remainingOptions =
      (selectedSetting?.max ?? 0) - selectedOptions.length;

    return !remainingOptions;
  };

  const handleUnselectOption = (
    optionTitle: Option['title'],
    selectedSetting: Setting
  ) => {
    const draft = { ...selectedSettings };
    const selectedSettingDraft = draft[selectedSetting.title] ?? [];

    if (selectedSettingDraft.includes(optionTitle)) {
      if (selectedSetting?.max === 1) {
        // Single option selection
        draft[selectedSetting.title] = [];
      } else {
        // Multiple option selection
        draft[selectedSetting.title] = selectedSettingDraft.filter(
          (title) => title !== optionTitle
        );
      }
    }

    setSelectedSettings(draft);
  };

  const handleChangeSettings = (
    optionTitle: Option['title'],
    selectedSetting: Setting
  ) => {
    if (selectedSetting.type !== 'options') {
      console.error('Option type not supported');
      return;
    }

    const selectedOption = (selectedSetting?.options ?? []).find(
      ({ title }) => title === optionTitle
    );

    if (!selectedSetting?.id || !selectedOption) return;

    const draft = { ...selectedSettings };
    let selectedOptions = draft[selectedSetting.title] ?? [];

    if (selectedSetting?.max === 1) {
      // Single option selection
      selectedOptions = selectedOptions.includes(optionTitle)
        ? []
        : [optionTitle];
    } else if (selectedSetting?.max) {
      // Multiple option selection
      const remainingOptions = selectedSetting.max - selectedOptions.length;

      if (remainingOptions > 0) {
        // Can continue selecting
        if (selectedOptions.includes(optionTitle)) {
          selectedOptions = selectedOptions.filter(
            (title) => title !== optionTitle
          );
        } else {
          selectedOptions.push(optionTitle);
        }
      } else if (selectedOptions.includes(optionTitle)) {
        // Selection limit reached
        selectedOptions = selectedOptions.filter(
          (title) => title !== optionTitle
        );
      }
    }

    draft[selectedSetting.title] = selectedOptions;

    // Remove empty options
    Object.entries(draft).forEach(([settingTitle, options]) => {
      if (!options.length) {
        delete draft[settingTitle];
      }
    });

    setSelectedSettings(draft);
  };

  const isSettingOptionSelected = (
    title: Option['title'],
    setting: Setting
  ) => {
    const settingOptions = selectedSettings[setting?.title ?? ''] ?? [];
    return settingOptions.includes(title);
  };

  if (!settings.length) {
    return null;
  }

  return (
    <Stack spacing={3}>
      {settings.map((setting) => (
        <Stack key={setting.title} spacing={1}>
          <Typography variant="h5" fontWeight={700}>{setting.title}</Typography>
          {setting?.options ? (
            <Stack direction={'row'} spacing={1}>
              {setting.options.map(({ title }, optionIndex) => (
                <Tooltip
                  arrow
                  placement="top"
                  key={`${title}-${optionIndex}`}
                  title="Haz alcanzado el máximo de selección"
                  disableHoverListener={!isLimitReached(setting, title)}
                >
                  <span>
                    <Chip
                      label={title}
                      variant={
                        (selectedSettings[setting?.title ?? ''] ?? []).includes(
                          title
                        )
                          ? 'contained'
                          : 'outlined'
                      }
                      color={
                        (selectedSettings[setting?.title ?? ''] ?? []).includes(
                          title
                        )
                          ? 'primary'
                          : 'default'
                      }
                      onClick={() => handleChangeSettings(title, setting)}
                      disabled={isLimitReached(setting, title)}
                      onDelete={
                        isSettingOptionSelected(title, setting)
                          ? () => handleUnselectOption(title, setting)
                          : undefined
                      }
                    />
                  </span>
                </Tooltip>
              ))}
            </Stack>
          ) : null}
        </Stack>
      ))}
    </Stack>
  );
};

export default ProductSettings;
